import type { Agent } from '@studio/models/agent';
import type { Room } from '@studio/models/room';
import { getLinkAgentIds } from '#~/agent/components/bridge/hooks.js';
import type { LinkRequest } from '#~/api/agent/index.js';
import {
  createAccountStudio,
  deleteUnlinkedRoom,
  fetchRequestLink,
  fetchRoomIdForAgentId,
  inspectInitialRoomInventory,
  linkAgent
} from '#~/api/agent/index.js';
import type { CreateAccountStudioResult } from '#~/api/agent/registry.js';
import { resolveLinkedAgentId, waitForLinkedAgentId } from '#~/electron/identity-store.js';
// Per rule: use-canonical-logger-not-console (ai.knowledge/platform/studio.client).
import { logger } from '#~/logger/index.js';
import * as persistenceLayer from '#~/persistence/index.js';
import { withStudioOperationDeadline } from './operation-deadline.js';
import type { AccountBootstrapPickerMode } from '#~/routes/root/account-bootstrap-decision.js';
import { toAccountStudioPickerState, type AccountStudioPickerRoom } from './bootstrap-state.js';

const CREATE_ACTION_KEY_PREFIX = 'account_studio_create_action';

const createActionStorageKey = (accountId: string | undefined): string =>
  `${CREATE_ACTION_KEY_PREFIX}:${accountId ?? 'unknown'}`;

/**
 * One in-flight picker create, durable across quit/relaunch.
 *
 * `idempotencyKey` makes a retry ADOPT the Studio the server already created
 * instead of provisioning another. `roomId` is recorded the moment the create
 * response lands and before the link starts, so an interruption anywhere in
 * the link/verify half leaves enough on disk to roll the orphan back later.
 */
export interface PendingAccountStudioCreate {
  readonly idempotencyKey: string;
  readonly roomId?: Room['id'] | undefined;
  /**
   * The attempt fully resolved (verified link, or a settled rollback) but the
   * record could not be removed. A completed record is inert: a reconcile only
   * retries the removal and never rolls its Studio back, and a retry never
   * adopts its key (without this fence, a stale record
   * outliving a successful link would delete a Studio the user then used).
   */
  readonly completed?: boolean | undefined;
}

// WHY the persistence layer and not window.localStorage: the packaged Electron
// renderer is served from `http://127.0.0.1:<ephemeral port>` (server.listen(0)),
// so its web origin — and therefore its localStorage — is brand new on every
// launch. A key kept there never survives the quit-and-relaunch this record
// exists for, and the server's same-key adoption silently degrades to "create
// another Studio" (QA: five orphans in five cycles). The persistence
// layer routes to electron-store in the main process (synchronous file write,
// origin-independent) and to localStorage on the web, matching the sibling
// auto-link create key (verify-auto-link/v2/auto-link-idempotency.ts).
// Per rules: investigate-before-adding, architecture-principles,
// settle-every-terminal-branch (ai.knowledge/global).
const isUuidLike = (value: string): boolean => /^[0-9a-f-]{16,}$/i.test(value.trim());

/** Read the durable pending-create record for this account, if any. */
export const readPendingAccountStudioCreate = async (
  accountId: string | undefined
): Promise<PendingAccountStudioCreate | undefined> => {
  const storageKey = createActionStorageKey(accountId);
  let raw: string | null;
  try {
    raw = await persistenceLayer.get(storageKey);
  } catch (cause) {
    logger.warning('Unable to read the pending Studio create record.', cause);
    return undefined;
  }
  if (raw === null || raw === '') {
    return adoptLegacyPendingAccountStudioCreate(accountId);
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      'idempotencyKey' in parsed &&
      typeof parsed.idempotencyKey === 'string' &&
      parsed.idempotencyKey !== ''
    ) {
      const roomId =
        'roomId' in parsed && typeof parsed.roomId === 'string' && parsed.roomId !== ''
          ? (parsed.roomId as Room['id'])
          : undefined;
      const completed = 'completed' in parsed && parsed.completed === true;
      return {
        idempotencyKey: parsed.idempotencyKey,
        ...(roomId === undefined ? {} : { roomId }),
        ...(completed ? { completed } : {})
      };
    }
  } catch {
    // Not JSON: fall through to the bare-key shape below.
  }
  // The previous format stored the bare action key. Honour it so an in-flight
  // attempt from before this record shape is still adopted, never duplicated.
  return isUuidLike(raw) ? { idempotencyKey: raw.trim() } : undefined;
};

/**
 * The previous implementation kept the bare action key in
 * `window.localStorage` under the same storage key (no prefix, no JSON). On
 * an origin that survives the update (the web app, or dev mode) that entry
 * may still describe an in-flight attempt; adopt it into the durable store so
 * the retry resumes the same Studio instead of creating another, then remove
 * the old entry so it is never read twice.
 */
const adoptLegacyPendingAccountStudioCreate = async (
  accountId: string | undefined
): Promise<PendingAccountStudioCreate | undefined> => {
  const storageKey = createActionStorageKey(accountId);
  let legacy: string | null = null;
  try {
    legacy = typeof window === 'undefined' ? null : window.localStorage.getItem(storageKey);
  } catch {
    return undefined;
  }
  if (legacy === null || !isUuidLike(legacy)) return undefined;
  const record: PendingAccountStudioCreate = { idempotencyKey: legacy.trim() };
  await writePendingAccountStudioCreate(accountId, record);
  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    // Best effort: a lingering legacy entry is harmless once the record exists.
  }
  logger.info('Adopted a legacy pending Studio create key into durable storage.');
  return record;
};

const writePendingAccountStudioCreate = async (
  accountId: string | undefined,
  record: PendingAccountStudioCreate
): Promise<void> => {
  try {
    await persistenceLayer.set(createActionStorageKey(accountId), JSON.stringify(record));
  } catch (cause) {
    // Best effort: the create still proceeds with this key; only the
    // cross-launch guarantee is lost, and the caller's error paths still run.
    logger.warning('Unable to persist the pending Studio create record.', cause);
  }
};

/**
 * Remove the durable record once the attempt has fully resolved either way.
 * If the removal fails, the record is rewritten as `completed` so it can never
 * act again: occupancy alone cannot tell an abandoned create from a Studio the
 * user finished setting up and later unlinked, so a live record surviving a
 * verified link would be a deletion waiting to happen. The next reconcile
 * retries the removal.
 */
export const clearPendingAccountStudioCreate = async (
  accountId: string | undefined
): Promise<void> => {
  const storageKey = createActionStorageKey(accountId);
  try {
    await persistenceLayer.remove(storageKey);
    return;
  } catch (cause) {
    logger.warning('Unable to clear the pending Studio create record; fencing it.', cause);
  }
  const current = await readPendingAccountStudioCreate(accountId);
  if (current === undefined || current.completed === true) return;
  try {
    await persistenceLayer.set(storageKey, JSON.stringify({ ...current, completed: true }));
  } catch (cause) {
    // Both writes failed: nothing durable can be changed right now. The next
    // reconcile re-reads the authoritative inventory before acting.
    logger.warning('Unable to fence the pending Studio create record.', cause);
  }
};

const resolveLinkRequest = async (
  expectedAgentId: Agent['id'] | undefined
): Promise<{ readonly agentId: Agent['id']; readonly requestId: LinkRequest['id'] }> => {
  // Fence the EXISTING identity before requesting a link. The auth sidecar
  // deliberately mints a fresh ID for a rebind; comparing that new ID with
  // the picker's old ID rejects valid account-switch/create/replace flows.
  if (expectedAgentId !== undefined) {
    const currentAgentId = await withStudioOperationDeadline(
      () => resolveLinkedAgentId(true),
      'checking the current Bridge'
    );
    if (currentAgentId !== expectedAgentId) {
      throw new Error('The local Bridge identity changed before the Studio operation.');
    }
  }

  // Share the picker's existing deadline with Electron so it can wait for the
  // notification receiver without spending the cloud mutation's own deadline.
  const timeoutMs = 30_000;
  const deadlineAt = Date.now() + timeoutMs;
  return withStudioOperationDeadline(
    () => getLinkAgentIds(true, 'auto', deadlineAt),
    'requesting a Bridge link',
    timeoutMs
  );
};

/**
 * The point where an operation commits to a Bridge link: the sidecar has
 * issued its link request and the cloud link mutation is the next call.
 * Callers use it to open their link telemetry with the request id the rest
 * of the link carries.
 */
export interface AccountStudioLinkAttempt {
  readonly agentId: Agent['id'];
  readonly requestId: LinkRequest['id'];
  readonly roomId: Room['id'];
}

export type AccountStudioLinkAttemptListener = (attempt: AccountStudioLinkAttempt) => void;

export interface AccountStudioLinkInput {
  readonly expectedAgentId?: Agent['id'] | undefined;
  /** The Desktop Bridges the user confirmed a 'replace_existing' link displaces. */
  readonly expectedDisplacedAgentIds?: readonly Agent['id'][] | undefined;
  /** The committed Desktop Bridges the user confirmed an 'add_existing' link joins. */
  readonly expectedExistingAgentIds?: readonly Agent['id'][] | undefined;
  /** Fires once, immediately before the cloud link mutation. */
  readonly onLinkAttempt?: AccountStudioLinkAttemptListener | undefined;
  readonly policy: 'require_available' | 'replace_existing' | 'add_existing';
  readonly roomId: Room['id'];
  readonly token: string;
  readonly verify?: VerifyLinkConvergenceOptions | undefined;
}

export interface AccountStudioLinkResult {
  readonly agentId: Agent['id'];
  readonly requestId: LinkRequest['id'];
}

// Read-after-write convergence budget for the post-link authoritative read.
// When verifyAccountStudioLink runs, linkAgent has already COMMITTED (its HTTP
// response arrived) and the local sidecar has confirmed the identity — so a
// 403 or a stale/absent room id from the replica-backed agents.get here is the
// eventual-consistency window, not a verdict: the agents/get Lambda's policy
// check precedes its existence lookup, so a not-yet-replicated fresh link
// reads as an in-band 403 (CI run 33212166552: 403 ~0.9s after the commit
// turned a SUCCESSFUL link into a permission notice). Same transient-vs-stable
// discrimination-on-time as the auto-link controller's stale-revalidate
// re-probe (DEFAULT_STALE_REVALIDATE_GRACE_MS); 10s mirrors the boot room-sync
// budget and costs nothing on the happy path (first read usually succeeds).
// Per rules: reference-rules-in-comments, ai-code-requires-tests
// (ai.knowledge/global); knowledge: mailroom-atob-decode-failure-diagnosis
// (ai.knowledge/platform).
const VERIFY_LINK_CONVERGENCE_TIMEOUT_MS = 10_000;
const VERIFY_LINK_CONVERGENCE_POLL_MS = 500;

/** Injectable so tests don't wall-clock wait (mirrors AutoLinkOptions). */
export interface VerifyLinkConvergenceOptions {
  readonly timeoutMs?: number | undefined;
  readonly pollMs?: number | undefined;
  readonly delayMs?: ((ms: number) => Promise<void>) | undefined;
}

// Structural 403 check (mirrors select-studio's apiCode): the registry client
// throws ApiError-shaped objects carrying a numeric `code`.
const isInBandForbidden = (cause: unknown): boolean =>
  cause !== null && typeof cause === 'object' && 'code' in cause && Number(cause.code) === 403;

/** Verify both local identity and authoritative room ownership after a link transition. */
export const verifyAccountStudioLink = async (
  expectedAgentId: Agent['id'],
  expectedRoomId: Room['id'],
  token: string,
  options?: VerifyLinkConvergenceOptions
): Promise<void> => {
  // The cloud link commits before the LOCAL sidecar completes its handshake,
  // so a one-shot force consult (`resolveLinkedAgentId(true)`) reads a
  // genuine-but-transient `unlinked` and fails verification for a link that
  // succeeded — the QA "Iris could not link this Bridge" repro. Wait (bounded)
  // for the sidecar to confirm instead.
  let linkedAgentId = await withStudioOperationDeadline(
    () => waitForLinkedAgentId(),
    'waiting for the linked Bridge'
  );
  // A rebind can leave the previous linked ID in the renderer until the IPC
  // push arrives. Keep consulting the live sidecar for the EXPECTED new ID,
  // not just any linked identity, within the same bounded convergence budget.
  const identityDeadline = Date.now() + (options?.timeoutMs ?? 10_000);
  while (linkedAgentId !== expectedAgentId && Date.now() < identityDeadline) {
    linkedAgentId = await withStudioOperationDeadline(
      () => resolveLinkedAgentId(true),
      'confirming the new Bridge identity',
      Math.max(1, identityDeadline - Date.now())
    );
    if (linkedAgentId !== expectedAgentId) {
      await (
        options?.delayMs ??
        ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)))
      )(Math.min(options?.pollMs ?? 500, Math.max(0, identityDeadline - Date.now())));
    }
  }
  if (linkedAgentId !== expectedAgentId) {
    throw new Error('The local Bridge did not confirm the expected identity.');
  }

  // AUTHORITATIVE half of the same race: poll the registry read until the
  // fresh link is visible or the convergence deadline passes. Only the two
  // known read-after-write shapes re-probe — an in-band 403 and a stale or
  // absent room id. Anything else (401, 500, transport) throws immediately,
  // and a STABLE 403/mismatch past the deadline is rethrown unchanged so a
  // genuine permission fact still surfaces as one.
  const timeoutMs = options?.timeoutMs ?? VERIFY_LINK_CONVERGENCE_TIMEOUT_MS;
  const pollMs = options?.pollMs ?? VERIFY_LINK_CONVERGENCE_POLL_MS;
  const delayMs =
    options?.delayMs ??
    ((ms: number): Promise<void> => {
      const { promise, resolve } = Promise.withResolvers<void>();
      setTimeout(resolve, ms);
      return promise;
    });
  const deadline = Date.now() + timeoutMs;

  for (;;) {
    let linkedRoomId: Room['id'] | undefined;
    try {
      linkedRoomId = await fetchRoomIdForAgentId(expectedAgentId, token);
    } catch (cause) {
      if (!isInBandForbidden(cause) || Date.now() >= deadline) throw cause;
      await delayMs(pollMs);
      continue;
    }
    if (linkedRoomId === expectedRoomId) return;
    if (Date.now() >= deadline) {
      throw new Error('The local Bridge did not confirm the target Studio link.');
    }
    await delayMs(pollMs);
  }
};

/** Apply one guarded link transition and verify its local and authoritative results. */
export const linkAndVerifyAccountStudio = async (
  input: AccountStudioLinkInput
): Promise<AccountStudioLinkResult> => {
  const { agentId, requestId } = await resolveLinkRequest(input.expectedAgentId);

  input.onLinkAttempt?.({ agentId, requestId, roomId: input.roomId });
  await linkAgent(agentId, requestId, input.roomId, input.token, undefined, undefined, {
    policy: input.policy,
    expectedDisplacedAgentIds:
      input.policy === 'replace_existing' ? (input.expectedDisplacedAgentIds ?? []) : [],
    expectedExistingAgentIds:
      input.policy === 'add_existing' ? (input.expectedExistingAgentIds ?? []) : []
  });
  await verifyAccountStudioLink(agentId, input.roomId, input.token, input.verify);

  return { agentId, requestId };
};

/**
 * What became of a created-but-not-linked Studio once the attempt stopped.
 *
 * - `deleted`: the Studio had no committed Bridge, so it was removed.
 * - `kept_linked`: the authoritative inventory shows THIS Bridge on it — the
 *   link committed even though the client never saw the confirmation.
 * - `kept_occupied`: another Bridge already committed to it; not ours to remove.
 * - `kept_billing_incomplete`: the service refused because the Studio's
 *   Stripe ids are not all persisted; deleting could strand a subscription.
 *   The Studio stays live and visible; the record is cleared (terminal).
 * - `missing`: it is no longer in the inventory (removed elsewhere).
 * - `reserved`: the service refused because a link HANDSHAKE holds the Studio
 *   (a provisional Bridge row, not a committed one). That handshake settles
 *   on the server within a bounded time — it commits or rolls back — so this
 *   is a transient state, not a verdict: the record stays and the caller is
 *   expected to reconcile again shortly (see select-studio's re-check), not
 *   wait for the user to act. Distinct from `unresolved` so callers can tell
 *   "ask again soon" from "something failed".
 * - `unresolved`: the authoritative read or the delete failed. The durable
 *   record stays so the next picker open, or the next create, resumes it.
 */
export type AccountStudioRollbackOutcome =
  | 'deleted'
  | 'kept_linked'
  | 'kept_occupied'
  | 'kept_billing_incomplete'
  | 'missing'
  | 'reserved'
  | 'unresolved';

export interface RollbackUnlinkedAccountStudioInput {
  readonly accountId: string | undefined;
  /** The Bridge the interrupted attempt was linking; a committed link to it counts as success. */
  readonly expectedAgentId?: Agent['id'] | undefined;
  readonly roomId: Room['id'];
  readonly token: string;
}

/**
 * Compensating half of create-and-link. Create and link are two
 * server transactions, so an interruption between them (link failure, tray
 * quit, force-quit, crash) leaves a Studio with no Bridge. Product decision
 * : a Studio is removed unless the full operation
 * completes.
 *
 * The inventory read here only classifies the cheap, non-destructive cases
 * (gone, linked to THIS Bridge, occupied by another). The delete itself is
 * conditional ON THE SERVER: rooms.delete with `onlyIfUnlinked` re-checks
 * occupancy under the same Studio row lock link admission takes, so a link
 * that commits after our read is never destroyed — the server answers
 * `kept_occupied` instead. A deployment that does not advertise
 * `setupRollback` gets no delete at all (an older rooms.delete would ignore
 * the flag), and the record stays for a later pass.
 *
 * Per rules: settle-every-terminal-branch, absent-data-gates-permissively
 * (ai.knowledge/global) — an unreadable inventory keeps the Studio and the
 * record; it never deletes on absence.
 */
export const rollbackUnlinkedAccountStudio = async (
  input: RollbackUnlinkedAccountStudioInput
): Promise<AccountStudioRollbackOutcome> => {
  let outcome: AccountStudioRollbackOutcome;
  try {
    const inventory = await inspectInitialRoomInventory(input.token);
    // Reuse the picker's own hex conversion so this compares the same ids the
    // cards render, rather than a second ad-hoc decoding of protobuf bytes.
    const room = toAccountStudioPickerState('bridge_setup', inventory.rooms, undefined).rooms.find(
      (candidate) => candidate.roomId === input.roomId
    );
    if (room === undefined) {
      outcome = 'missing';
    } else if (
      input.expectedAgentId !== undefined &&
      room.activeBridges.some((bridge) => bridge.id === input.expectedAgentId)
    ) {
      outcome = 'kept_linked';
    } else if (room.activeBridges.length > 0) {
      outcome = 'kept_occupied';
    } else if (!inventory.capabilities.setupRollback) {
      // Fail closed: never send a flag this server cannot honour.
      logger.info('Service does not advertise setup rollback; keeping the Studio and its record.', {
        roomId: input.roomId
      });
      return 'unresolved';
    } else {
      const verdict = await deleteUnlinkedRoom(input.roomId, input.token);
      if (verdict === 'kept_billing_incomplete') {
        // Terminal: the service will refuse this Studio every time until its
        // billing is repaired by hand. Log loudly, keep the Studio visible,
        // and stop retrying so the picker does not hammer the endpoint.
        logger.warning(
          'Setup rollback refused: Studio billing is incomplete; leaving the Studio in place.',
          { roomId: input.roomId }
        );
      }
      if (verdict === 'kept_reserved') {
        // A link handshake is mid-flight on this Studio. It may still fail
        // and roll its reservation back, which would leave the Studio empty
        // again — so this is not a verdict yet. Keep the record and report
        // the transient state so the picker re-checks soon: in the quit-
        // mid-link story the reservation is usually THIS computer's own
        // escaped link request, which the server rolls back within its
        // handshake budget (~20s) once the Bridge never shows up. Without a
        // prompt re-check the orphan sits on the grid until the user acts
        // (observed: `KEPT_RESERVED` 10s after the create, reservation rolled
        // back 16s later, never re-asked).
        logger.info(
          'Studio is reserved by an in-flight link; keeping its record and re-checking shortly.',
          {
            roomId: input.roomId
          }
        );
        return 'reserved';
      }
      outcome = verdict;
    }
  } catch (cause) {
    logger.warning(
      'Unable to roll back the unlinked Studio; keeping its record for a later reconcile.',
      {
        roomId: input.roomId,
        cause
      }
    );
    return 'unresolved';
  }

  logger.info('Reconciled an interrupted Studio create.', { roomId: input.roomId, outcome });
  await clearPendingAccountStudioCreate(input.accountId);
  return outcome;
};

/**
 * Retire the pending-create record if it names `roomId`. A create
 * whose link half failed leaves its Studio on the grid as an ordinary
 * available card; when the user then links that card successfully through
 * the link path, the Studio is no longer an abandoned create. Without this,
 * the record would outlive a successful link and the next reconcile after an
 * unlink would delete a Studio that had been used. A record naming a different
 * Studio is left alone — that one is still an orphan.
 */
export const retirePendingAccountStudioCreateForRoom = async (
  accountId: string | undefined,
  roomId: Room['id']
): Promise<boolean> => {
  const pending = await readPendingAccountStudioCreate(accountId);
  if (pending?.roomId !== roomId) return false;
  logger.info('Retiring the pending Studio create record: its Studio was linked directly.', {
    roomId
  });
  await clearPendingAccountStudioCreate(accountId);
  return true;
};

export interface ReconcilePendingAccountStudioCreateInput {
  readonly accountId: string | undefined;
  readonly expectedAgentId?: Agent['id'] | undefined;
  readonly token: string;
}

/**
 * Relaunch-time counterpart of {@link rollbackUnlinkedAccountStudio}: if a
 * previous session recorded a create that never reached verified success,
 * settle it now. `none` means nothing was pending. A record without a room id
 * (the create request was interrupted before its response) is left in place —
 * it is exactly what makes the next create adopt any Studio the server did
 * commit, and there is nothing authoritative to delete yet.
 */
export const reconcilePendingAccountStudioCreate = async (
  input: ReconcilePendingAccountStudioCreateInput
): Promise<AccountStudioRollbackOutcome | 'none'> => {
  const pending = await readPendingAccountStudioCreate(input.accountId);
  if (pending === undefined) return 'none';
  if (pending.completed === true) {
    // Resolved earlier; only the removal is outstanding. Never roll back.
    await clearPendingAccountStudioCreate(input.accountId);
    return 'none';
  }
  if (pending.roomId === undefined) return 'unresolved';
  return rollbackUnlinkedAccountStudio({
    accountId: input.accountId,
    expectedAgentId: input.expectedAgentId,
    roomId: pending.roomId,
    token: input.token
  });
};

export interface CreateAndLinkAccountStudioInput {
  readonly accountId: string | undefined;
  readonly expectedAgentId?: Agent['id'] | undefined;
  readonly onCreated?: ((created: CreateAccountStudioResult) => void) | undefined;
  /**
   * Fires after a post-create link or verify failure with what became of the
   * new Studio, before the original failure is rethrown. `deleted`/`missing`
   * mean no Studio remains, so the UI should present a create failure.
   */
  readonly onLinkFailed?: ((outcome: AccountStudioRollbackOutcome) => void) | undefined;
  /** Fires once, after the create and immediately before the cloud link mutation. */
  readonly onLinkAttempt?: AccountStudioLinkAttemptListener | undefined;
  readonly token: string;
  readonly verify?: VerifyLinkConvergenceOptions | undefined;
}

export type CreateAndLinkAccountStudioResult = CreateAccountStudioResult & {
  readonly agentId: Agent['id'];
  readonly requestId: LinkRequest['id'];
};

/**
 * Create an idempotent account Studio, move the local Bridge to it, and verify
 * the move. The durable pending record is written BEFORE the create request
 * (so a retry adopts the same Studio) and updated with the room id BEFORE the
 * link (so an interruption can be rolled back); it is cleared only after
 * verified success or a settled rollback.
 */
export const createAndLinkAccountStudio = async (
  input: CreateAndLinkAccountStudioInput
): Promise<CreateAndLinkAccountStudioResult> => {
  const { agentId, requestId } = await resolveLinkRequest(input.expectedAgentId);
  const localBridge = await fetchRequestLink(requestId, input.token);

  if (localBridge.name.trim() === '') {
    throw new Error('The local Bridge name is unavailable.');
  }

  // Mint (or resume) the action key as late as possible — right before the
  // create request — so the window in which a record has no room id is just
  // that one round trip.
  const stored = await readPendingAccountStudioCreate(input.accountId);
  // A completed record belongs to an attempt that finished; adopting its key
  // would hand back THAT Studio instead of creating a new one.
  const pending: PendingAccountStudioCreate =
    stored !== undefined && stored.completed !== true
      ? stored
      : { idempotencyKey: window.crypto.randomUUID() };
  await writePendingAccountStudioCreate(input.accountId, pending);

  const created = await createAccountStudio(localBridge.name, input.token, pending.idempotencyKey);
  await writePendingAccountStudioCreate(input.accountId, {
    idempotencyKey: pending.idempotencyKey,
    roomId: created.roomId
  });
  input.onCreated?.(created);

  try {
    input.onLinkAttempt?.({ agentId, requestId, roomId: created.roomId });
    await linkAgent(agentId, requestId, created.roomId, input.token, undefined, undefined, {
      policy: 'require_available'
    });
    await verifyAccountStudioLink(agentId, created.roomId, input.token, input.verify);
  } catch (cause) {
    const outcome = await rollbackUnlinkedAccountStudio({
      accountId: input.accountId,
      expectedAgentId: agentId,
      roomId: created.roomId,
      token: input.token
    });
    input.onLinkFailed?.(outcome);
    throw cause;
  }

  await clearPendingAccountStudioCreate(input.accountId);
  return { ...created, agentId, requestId };
};

export type AccountStudioSelectionDecision =
  | { readonly kind: 'capacity_unknown'; readonly target: AccountStudioPickerRoom }
  | { readonly kind: 'at_capacity'; readonly target: AccountStudioPickerRoom }
  | { readonly kind: 'confirm_add'; readonly target: AccountStudioPickerRoom }
  /**
   * Occupied Studio while adding another computer is unavailable: linking
   * here replaces its Desktop Bridge, so the picker asks first.
   */
  | { readonly kind: 'confirm_replace'; readonly target: AccountStudioPickerRoom }
  | { readonly kind: 'denied' }
  | { readonly kind: 'link_available'; readonly target: AccountStudioPickerRoom }
  | { readonly kind: 'missing' }
  | { readonly kind: 'open'; readonly target: AccountStudioPickerRoom };

export interface AccountStudioSelectionOptions {
  readonly multiBridgeEnabled?: boolean | undefined;
  /** Server-advertised desktop_add_v1 (ENG-4483 W3); absent means unsupported. */
  readonly desktopAddSupported?: boolean | undefined;
}

/** Decide picker behavior from authoritative Bridge occupancy, never card presentation alone. */
export const decideAccountStudioSelection = (
  mode: AccountBootstrapPickerMode,
  target: AccountStudioPickerRoom | undefined,
  localAgentId: Agent['id'] | undefined,
  options?: AccountStudioSelectionOptions
): AccountStudioSelectionDecision => {
  if (target === undefined) return { kind: 'missing' };

  // Access mode is navigation-only; Bridge occupancy and link permissions are informational.
  if (mode === 'studio_access') return { kind: 'open', target };

  const isLocalTarget =
    localAgentId !== undefined && target.activeBridges.some((bridge) => bridge.id === localAgentId);
  if (isLocalTarget) return { kind: 'open', target };

  // Permission is checked before either a first link or an occupied-Studio
  // confirmation, so no affordance can imply authority the account lacks.
  if (!target.canLinkBridge) return { kind: 'denied' };

  if (target.activeBridges.length === 0) return { kind: 'link_available', target };

  // A Studio holds one Desktop Bridge until adding is enabled on BOTH sides
  // (client flag and server capability). Until then, taking an occupied
  // Studio replaces its Desktop Bridge, and only after the user confirms.
  if (options?.multiBridgeEnabled !== true || options?.desktopAddSupported !== true) {
    return { kind: 'confirm_replace', target };
  }

  const capacity = target.desktopBridgeCapacity;
  if (capacity === undefined || capacity.kind === 'unknown') {
    return { kind: 'capacity_unknown', target };
  }
  if (capacity.kind === 'at_capacity') {
    return { kind: 'at_capacity', target };
  }
  // Treat malformed or stale "available" limits as closed. The service,
  // not the client, owns the entitlement boundary.
  if (!Number.isInteger(capacity.maximum) || capacity.maximum <= target.activeBridges.length) {
    return { kind: 'at_capacity', target };
  }
  return { kind: 'confirm_add', target };
};
